import * as RadixDialog from "@radix-ui/react-dialog";
import { LogOut, Menu as MenuIcon, PanelsTopLeft } from "lucide-react";
import { startTransition, useState } from "react";
import { Outlet, useMatch, useNavigate } from "react-router";

import { exitStaffView, getMe } from "@/api/client";
import type { Me } from "@/api/client";
import { useAuth, useMe } from "@/auth/AuthProvider";
import { Sidebar } from "@/shell/Sidebar";
import { readCollapsed, saveCollapsed } from "@/shell/sidebarState";
import { Button } from "@/ui/Button";

/** Darkstar staff viewing this workspace (ADR-0018): whose it is, until when, and the
 * way back to the console. */
function StaffViewBanner({ me, until }: { me: Me; until: string }) {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const leave = async () => {
    setLeaving(true);
    try {
      await exitStaffView();
      const who = await getMe();
      // One render for both, or the guard sends staff to the console's home first: the
      // router navigates in a transition.
      startTransition(() => {
        adopt(who);
        void navigate(`/platform/orgs/${me.organization.id}`, { replace: true });
      });
    } catch {
      setLeaving(false);
    }
  };
  const time = new Date(until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-accent/40 bg-accent-soft px-4 py-2 text-[13px] text-ink"
    >
      <span className="min-w-0 flex-1">
        Viewing {me.organization.name} as Darkstar staff, read-only, until {time}.
      </span>
      <Button size="sm" variant="secondary" disabled={leaving} onClick={() => void leave()}>
        <LogOut aria-hidden="true" />
        Exit to platform
      </Button>
    </div>
  );
}

/**
 * Every page sits in this frame: the sidebar on the left, the page on the right. The
 * take-off and the type review need the width for drawings, so the sidebar is held
 * collapsed there whatever the saved choice. Below `md` it is a drawer.
 */
export function AppShell() {
  const me = useMe();
  const [chosen, setChosen] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const onTakeoff = useMatch("/documents/:id/runs/:runId/takeoff");
  const onTypes = useMatch("/documents/:id/runs/:runId/types/*");
  const forced = Boolean(onTakeoff ?? onTypes);
  const collapsed = forced || chosen;

  const toggle = () => {
    const next = !chosen;
    saveCollapsed(next);
    setChosen(next);
  };

  return (
    <div className="flex h-dvh bg-bg">
      <aside className="hidden h-full shrink-0 md:block" aria-label="Sidebar">
        <Sidebar me={me} collapsed={collapsed} {...(forced ? {} : { onToggle: toggle })} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 md:hidden">
          <RadixDialog.Root open={drawer} onOpenChange={setDrawer}>
            <RadixDialog.Trigger asChild>
              <Button variant="ghost" size="sm" className="w-8 px-0" aria-label="Open the menu">
                <MenuIcon aria-hidden="true" />
              </Button>
            </RadixDialog.Trigger>
            <RadixDialog.Portal>
              <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
              <RadixDialog.Content
                aria-describedby={undefined}
                className="fixed inset-y-0 left-0 z-50 focus:outline-none"
              >
                <RadixDialog.Title className="sr-only">Menu</RadixDialog.Title>
                <Sidebar me={me} collapsed={false} onNavigate={() => setDrawer(false)} />
              </RadixDialog.Content>
            </RadixDialog.Portal>
          </RadixDialog.Root>
          <span
            aria-hidden="true"
            className="grid size-7 place-items-center rounded-md bg-accent text-accent-ink"
          >
            <PanelsTopLeft className="size-4" />
          </span>
          <span className="truncate text-sm font-semibold text-ink">{me.organization.name}</span>
        </div>
        {me.staff_view ? (
          <StaffViewBanner me={me} until={me.staff_view.until} />
        ) : me.read_only ? (
          <p
            role="status"
            className="shrink-0 border-b border-warn/40 bg-warn-soft px-4 py-2 text-[13px] text-ink"
          >
            Your organization&apos;s licence has ended. Everything can be read, and nothing can be
            changed until it is renewed.
          </p>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
